import type { Content, TDocumentDefinitions } from "pdfmake/interfaces";
import { MIN_SCALE, PAGE, CONTENT_AREA, TOP_BOX, BOTTOM_BOX, BOTTOM_PREVIEW_HEIGHT_PX } from "../constant";
import type { ViewName } from "../types";
import { computeViewSizesInMillimeters, getViewsToRender, maxScaleForArea } from "../layout/scale";
import { chooseScaleAndPages, packPagesWithExactSizes } from "../layout/pagePacking";
import { buildAllViewsSvg, buildSideViewSvg, buildTopViewSvg } from "../render/svgBuilders";
import type { LegendProvider } from "../legend/LegendProvider";
import { HttpLegendProvider } from "../legend/LegendProvider";
import type { PdfProvider } from "../pdf/PdfProvider";
import { PdfMakeAdapter } from "../pdf/PdfMakeAdapter";
import { rotateSvgMarkup90ccw } from "../utils/svg";
import { normalizeKey } from "../utils/math";
import { registerAllItemRenderers } from "../drawing/items/register";
import { buildTopPreview } from "../render/miniTopPreview";

registerAllItemRenderers();

function buildLegendIndexByName(sectionsForView: any[]) {
    const toArr = <T>(v: T | T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);
    const rawNames = sectionsForView
        .flatMap((s) => toArr(s.items))
        .map((it) => (it?.name ?? "").trim())
        .filter((n) => n.length > 0) as string[];

    const counts = new Map<string, { name: string; count: number }>();
    for (const name of rawNames) {
        const key = normalizeKey(name);
        const cur = counts.get(key);
        if (cur) cur.count += 1;
        else counts.set(key, { name, count: 1 });
    }

    const legend = new Map<string, number>();
    let i = 1;
    // @ts-ignore
    for (const [k, v] of counts.entries()) {
        legend.set(k, i++);
    }
    return { legend, counts };
}

export type GenerateBlueprintOptions = {
    minScale?: number;
    legendUrl?: string;
    includeEmptyViews?: boolean;
    eachViewOnSeparatePage?: boolean;
};

export const DEFAULT_LEGEND_URL =
    typeof process !== "undefined" && process.env.NODE_ENV === "development" ? "/legend.svg" : "/conf/app/legend.svg";

export async function generateBlueprintPdf(
    roomLengthMm: number,
    roomDepthMm: number,
    roomHeightMm: number,
    sections: any[],
    options?: GenerateBlueprintOptions,
    deps?: { pdf?: PdfProvider; legend?: LegendProvider }
) {
    const legendProvider = deps?.legend ?? new HttpLegendProvider(options?.legendUrl ?? DEFAULT_LEGEND_URL);
    const pdf = deps?.pdf ?? new PdfMakeAdapter();

    const sizesMm = computeViewSizesInMillimeters(roomLengthMm, roomDepthMm, roomHeightMm);
    const allViewsOrder: ViewName[] = ["top", "front", "back", "left", "right"] as ViewName[];
    const viewsToRender = getViewsToRender(sections, options?.includeEmptyViews)
        .filter((v: ViewName, i: number, arr: ViewName[]) => arr.indexOf(v) === i)
        .filter((v: ViewName) => allViewsOrder.includes(v));

    const frontSections = sections.filter((s: any) => s.side === "front");
    const backSections = sections.filter((s: any) => s.side === "back");
    const leftSections = sections.filter((s: any) => s.side === "left");
    const rightSections = sections.filter((s: any) => s.side === "right");

    const legendSvg = await legendProvider.getLegendSvg();

    if (options?.eachViewOnSeparatePage) {
        const content: Content[] = [];

        const buildSingleView = (name: ViewName, scale: number, legendIndex?: Map<string, number>) => {
            switch (name) {
                case "top":
                    return buildTopViewSvg(roomLengthMm, roomDepthMm, sections, scale);
                case "front":
                    return buildSideViewSvg(
                        roomLengthMm,
                        roomHeightMm,
                        frontSections,
                        scale,
                        undefined,
                        "front",
                        legendIndex
                    );
                case "back":
                    return buildSideViewSvg(
                        roomLengthMm,
                        roomHeightMm,
                        backSections,
                        scale,
                        undefined,
                        "back",
                        legendIndex
                    );
                case "left":
                    return buildSideViewSvg(
                        roomDepthMm,
                        roomHeightMm,
                        leftSections,
                        scale,
                        undefined,
                        "left",
                        legendIndex
                    );
                case "right":
                    return buildSideViewSvg(
                        roomDepthMm,
                        roomHeightMm,
                        rightSections,
                        scale,
                        undefined,
                        "right",
                        legendIndex
                    );
            }
        };
        let topRotatedCW = false;
        viewsToRender.forEach((viewName, idx) => {
            const perViewScale = Math.max(
                MIN_SCALE,
                maxScaleForArea(sizesMm[viewName].w, sizesMm[viewName].h, CONTENT_AREA.width, CONTENT_AREA.height)
            );

            let legendIndexByName: Map<string, number> | undefined = undefined;
            if (viewName !== "top") {
                const sectionsForView =
                    viewName === "front"
                        ? frontSections
                        : viewName === "back"
                        ? backSections
                        : viewName === "left"
                        ? leftSections
                        : viewName === "right"
                        ? rightSections
                        : [];
                legendIndexByName = buildLegendIndexByName(sectionsForView).legend;
            }

            const built = buildSingleView(viewName, perViewScale, legendIndexByName)!;

            if (idx > 0) content.push({ text: "", pageBreak: "before" } as any);

            if (idx === 0 && viewName === "top") {
                const rotated = rotateSvgMarkup90ccw(built.svg, built.wPx, built.hPx);

                const fitUnrot = Math.min(CONTENT_AREA.width / built.wPx, CONTENT_AREA.height / built.hPx);
                const fitRot = Math.min(CONTENT_AREA.width / rotated.wPx, CONTENT_AREA.height / rotated.hPx);
                topRotatedCW = fitRot > fitUnrot;
                const choice = fitRot > fitUnrot ? rotated : built;
                const chosenFit = Math.min(CONTENT_AREA.width / choice.wPx, CONTENT_AREA.height / choice.hPx);
                const widthToRender = Math.min(choice.wPx * chosenFit, CONTENT_AREA.width);

                content.push({
                    svg: choice.svg,
                    width: widthToRender,
                    absolutePosition: { x: PAGE.margins.l, y: PAGE.margins.t },
                } as any);
            } else {
                const fit = Math.min(CONTENT_AREA.width / built.wPx, CONTENT_AREA.height / built.hPx);
                content.push({
                    svg: built.svg,
                    width: Math.min(built.wPx * fit, CONTENT_AREA.width),
                    absolutePosition: { x: PAGE.margins.l, y: PAGE.margins.t },
                } as any);
            }

            if (options?.eachViewOnSeparatePage && BOTTOM_PREVIEW_HEIGHT_PX > 0) {
                const stripTopY = PAGE.height - (12 + BOTTOM_PREVIEW_HEIGHT_PX);
                const GAP = 46;

                const textW = viewName === "top" ? 0 : Math.max(0, CONTENT_AREA.width - GAP);
                const textX = PAGE.margins.l;
                const miniX = PAGE.margins.l + CONTENT_AREA.width - 240 * 2;

                const mini = buildTopPreview(
                    roomLengthMm,
                    roomDepthMm,
                    240,
                    BOTTOM_PREVIEW_HEIGHT_PX,
                    viewName === "front" || viewName === "back" || viewName === "left" || viewName === "right"
                        ? (viewName as "front" | "back" | "left" | "right")
                        : undefined,
                    topRotatedCW ? "cw" : "auto"
                );

                content.push({
                    svg: mini.svg,
                    width: 220,
                    absolutePosition: { x: miniX, y: stripTopY },
                } as any);

                if (viewName !== "top" && textW > 0) {
                    const toArr = <T>(v: T | T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);
                    const sectionsForView =
                        viewName === "front"
                            ? frontSections
                            : viewName === "back"
                            ? backSections
                            : viewName === "left"
                            ? leftSections
                            : viewName === "right"
                            ? rightSections
                            : [];

                    const rawNames = sectionsForView
                        .flatMap((s) => toArr(s.items))
                        .map((it) => (it?.name ?? "").trim())
                        .filter((n) => n.length > 0) as string[];

                    const { legend: legendIndexByName, counts } = buildLegendIndexByName(sectionsForView);

                    const values = Array.from(counts.values());
                    const lines = values.map(({ name, count }, i) => (count > 1 ? `${name} x(${count})` : name));

                    for (const name of rawNames) {
                        const key = name.replace(/\s+/g, " ").toLowerCase();
                        const cur = counts.get(key);
                        if (cur) cur.count += 1;
                        else counts.set(key, { name, count: 1 });
                    }

                    if (lines.length) {
                        const fontSize = 8;
                        const lineHeight = 0.95;
                        const linePx = fontSize * lineHeight;

                        const availH = BOTTOM_PREVIEW_HEIGHT_PX - 12;
                        const maxPerCol = Math.max(1, Math.floor(availH / linePx));
                        const colCount = 2;

                        const cols: string[] = [];
                        for (let c = 0; c < colCount; c++) {
                            const start = c * maxPerCol;
                            const end = Math.min(start + maxPerCol, lines.length);
                            const chunk = lines.slice(start, end);
                            const text = chunk.map((n, i) => `${start + i + 1}. ${n}`).join("\n");
                            cols.push(text);
                        }

                        const widths = Array(colCount).fill(Math.floor(textW / colCount));

                        content.push({
                            table: {
                                widths,
                                body: [
                                    cols.map((t) => ({ text: t, fontSize, lineHeight, color: "#333", noWrap: false })),
                                ],
                            },
                            layout: "noBorders",
                            absolutePosition: {
                                x: textX - BOTTOM_PREVIEW_HEIGHT_PX / 2,
                                y: stripTopY - BOTTOM_PREVIEW_HEIGHT_PX / 2,
                            },
                            unbreakable: true,
                        } as any);
                    }
                }
            }

            if (legendSvg)
                content.push({
                    svg: legendSvg,
                    absolutePosition: { x: 0, y: 0 },
                    width: PAGE.width,
                    height: PAGE.height,
                } as any);
        });

        const doc: TDocumentDefinitions = {
            pageSize: "A4",
            pageOrientation: "landscape",
            pageMargins: [PAGE.margins.l, PAGE.margins.t, PAGE.margins.r, PAGE.margins.b],
            content,
            defaultStyle: { font: "Roboto" },
        };
        pdf.open(doc);
        return;
    }

    const minScale = Math.max(0.0001, options?.minScale ?? MIN_SCALE);
    const { scale } = chooseScaleAndPages(viewsToRender, sizesMm, minScale);

    const svgsByView = buildAllViewsSvg(roomLengthMm, roomDepthMm, roomHeightMm, sections, scale);
    const sizesInPixels: Record<ViewName, { w: number; h: number }> = {
        top: { w: svgsByView.top.wPx, h: svgsByView.top.hPx },
        front: { w: svgsByView.front.wPx, h: svgsByView.front.hPx },
        back: { w: svgsByView.back.wPx, h: svgsByView.back.hPx },
        left: { w: svgsByView.left.wPx, h: svgsByView.left.hPx },
        right: { w: svgsByView.right.wPx, h: svgsByView.right.hPx },
    };

    const pages = packPagesWithExactSizes(viewsToRender, sizesInPixels);
    const content: Content[] = [];

    pages.forEach((page) => {
        const hasAnything = page.top.length > 0 || page.bottom.length > 0;
        if (!hasAnything) return;
        if (content.length > 0) content.push({ text: "", pageBreak: "before" } as any);

        page.top.forEach((placement) => {
            content.push({
                svg: svgsByView[placement.name].svg,
                width: Math.min(sizesInPixels[placement.name].w, TOP_BOX.width),
                absolutePosition: { x: PAGE.margins.l + placement.x, y: PAGE.margins.t + placement.y },
            } as any);
        });

        page.bottom.forEach((placement) => {
            content.push({
                svg: svgsByView[placement.name].svg,
                width: Math.min(sizesInPixels[placement.name].w, BOTTOM_BOX.width),
                absolutePosition: { x: PAGE.margins.l + placement.x, y: PAGE.margins.t + TOP_BOX.height + placement.y },
            } as any);
        });

        if (legendSvg)
            content.push({
                svg: legendSvg,
                absolutePosition: { x: 0, y: 0 },
                width: PAGE.width,
                height: PAGE.height,
            } as any);
    });

    const doc: TDocumentDefinitions = {
        pageSize: "A4",
        pageOrientation: "landscape",
        pageMargins: [PAGE.margins.l, PAGE.margins.t, PAGE.margins.r, PAGE.margins.b],
        content,
        defaultStyle: { font: "Roboto" },
    };
    pdf.open(doc);
}
